import React, { useEffect, useState, useCallback } from "react";
import { HardDrive, Check, Sparkles, Loader2, ExternalLink, Crown, X } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";

const fmtGB = (bytes) => {
  const gb = bytes / (1024 ** 3);
  if (gb < 0.1) return `${(bytes / (1024 ** 2)).toFixed(0)} MB`;
  return `${gb.toFixed(1)} GB`;
};

export const BeaconHosting = () => {
  const [data, setData] = useState(null);
  const [showPlans, setShowPlans] = useState(false);
  const [cycle, setCycle] = useState("month");
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try { const { data: d } = await api.get("/beacon/billing/me"); setData(d); if (d?.hosting?.cycle) setCycle(d.hosting.cycle); }
    catch { /* noop */ }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Handle Stripe checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("checkout");
    const sid = params.get("session_id");
    if (!status) return;
    window.history.replaceState({}, "", "/beacon");
    if (status === "cancel") { toast("Checkout cancelled"); return; }
    if (status === "success" && sid) {
      let tries = 0;
      const poll = async () => {
        tries++;
        try {
          const { data: s } = await api.get(`/beacon/billing/status/${sid}`);
          if (s.payment_status === "paid") { toast.success("Your Beacon hosting is active"); load(); return; }
        } catch { /* noop */ }
        if (tries < 8) setTimeout(poll, 1500); else load();
      };
      toast("Confirming your subscription…");
      poll();
    }
  }, [load]);

  const subscribe = async (tier) => {
    setBusy(tier);
    // Tier change on an active sub, no redirect needed
    if (data?.hosting?.status === "active") {
      try {
        await api.post("/beacon/billing/change-tier", { tier, cycle });
        toast.success("Plan updated");
        setShowPlans(false); load();
      } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
      finally { setBusy(""); }
      return;
    }
    // New subscription -> Stripe Checkout. Open a tab synchronously so it isn't
    // blocked and so Stripe never loads inside the preview iframe (Stripe blocks framing).
    const tab = window.open("about:blank", "_blank");
    try {
      const { data: r } = await api.post("/beacon/billing/checkout", { tier, cycle, origin_url: window.location.origin });
      if (tab) { tab.location.href = r.url; toast("Opened secure checkout in a new tab"); }
      else { window.location.href = r.url; }
    } catch (e) {
      if (tab) tab.close();
      toast.error(e?.response?.data?.detail || "Something went wrong");
    } finally { setBusy(""); }
  };

  const manage = async () => {
    setBusy("portal");
    const tab = window.open("about:blank", "_blank");
    try {
      const { data: r } = await api.post("/beacon/billing/portal", { origin_url: window.location.origin });
      if (tab) { tab.location.href = r.url; } else { window.location.href = r.url; }
    } catch (e) {
      if (tab) tab.close();
      toast.error(e?.response?.data?.detail || "Unable to open billing");
    } finally { setBusy(""); }
  };

  if (!data || !data.has_beacon) return null;

  const st = data.storage;
  const pct = Math.min(100, Math.round((st.used_bytes / st.cap_bytes) * 100));
  const active = data.hosting.status === "active";
  const pastDue = data.hosting.status === "past_due";
  const curTier = data.hosting.tier;

  return (
    <div className="border border-white/10 bg-[#121212] p-5 mb-6" data-testid="beacon-hosting">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h2 className="font-body text-lg text-white flex items-center gap-2"><HardDrive size={17} className="text-[#14A89A]" /> Hosting &amp; storage</h2>
        <div className="flex items-center gap-2">
          {data.is_founding && (
            <span data-testid="founding-badge" className="flex items-center gap-1 text-[10px] tracking-[0.15em] uppercase px-2 py-0.5 border border-amber-400/50 text-amber-300 font-body"><Crown size={11} /> Founding · 40% off</span>
          )}
          {active
            ? <button onClick={manage} data-testid="beacon-manage-plan" className="flex items-center gap-1.5 border border-white/15 text-zinc-300 px-3 py-2 font-body text-xs hover:bg-white/5">{busy === "portal" ? <Loader2 size={13} className="animate-spin" /> : <ExternalLink size={13} />} Manage plan</button>
            : <button onClick={() => setShowPlans(true)} data-testid="beacon-choose-plan" className="bg-[#14A89A] text-[#0A0A0A] font-semibold px-4 py-2 font-body text-xs hover:brightness-110">{data.window_open ? "See plans" : "Choose a plan"}</button>}
        </div>
      </div>

      {/* Storage meter */}
      <div className="mb-3" data-testid="storage-meter">
        <div className="flex justify-between font-body text-xs text-zinc-400 mb-1.5">
          <span>{fmtGB(st.used_bytes)} used</span>
          <span>{st.cap_gb} GB {curTier ? "" : "(launch)"}</span>
        </div>
        <div className="h-2 bg-white/10 overflow-hidden rounded-full">
          <div className={`h-full rounded-full transition-all ${st.over ? "bg-red-500" : pct > 85 ? "bg-amber-400" : "bg-[#14A89A]"}`} style={{ width: `${Math.max(2, pct)}%` }} />
        </div>
        {st.over && <p className="font-body text-xs text-red-400 mt-1.5">You're over your plan's storage. Upgrade a tier to keep things smooth.</p>}
      </div>

      {/* Status line */}
      <p className="font-body text-xs text-zinc-500">
        {data.window_open
          ? "Free Launch Window is on, hosting is on us, and you're locked in as a Founding Beacon (40% off for life once pricing begins)."
          : active
            ? <>Active plan: <span className="text-zinc-300">{data.plans.find(p => p.tier === curTier)?.label}</span>, billed {data.hosting.cycle === "year" ? "annually" : "monthly"}.{data.hosting.current_period_end ? ` Renews ${new Date(data.hosting.current_period_end).toLocaleDateString()}.` : ""}</>
            : pastDue
              ? <span className="text-amber-400">Payment failed: your Beacon stays live for 14 days. Please update your payment method.</span>
              : "Choose a plan to keep your Beacon live."}
      </p>
      <p className="font-body text-[11px] text-zinc-600 mt-2">Every tier includes your private community (Beacon Hearth), no extra charge, no member limit.</p>

      {showPlans && <PlansModal data={data} cycle={cycle} setCycle={setCycle} onClose={() => setShowPlans(false)} onPick={subscribe} busy={busy} curTier={active ? curTier : null} />}
    </div>
  );
};

const PlansModal = ({ data, cycle, setCycle, onClose, onPick, busy, curTier }) => {
  const founding = data.is_founding;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="beacon-plans-modal">
      <div className="bg-[#0F0F0F] border border-white/10 w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-display text-2xl text-white">Choose your hosting</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="plans-close"><X size={20} /></button>
        </div>
        {founding && <p className="font-body text-xs text-amber-300 flex items-center gap-1 mb-4"><Crown size={12} /> Your Founding 40% discount is applied automatically to any tier.</p>}

        <div className="flex items-center justify-center gap-1 mb-6 mt-2">
          {["month", "year"].map((c) => (
            <button key={c} onClick={() => setCycle(c)} data-testid={`cycle-${c}`}
              className={`px-4 py-2 font-body text-sm border transition-colors ${cycle === c ? "bg-[#14A89A] text-[#0A0A0A] border-[#14A89A] font-semibold" : "border-white/15 text-zinc-300 hover:bg-white/5"}`}>
              {c === "month" ? "Monthly" : "Annual"}{c === "year" && <span className="ml-1 text-[10px] opacity-80">2 months free</span>}
            </button>
          ))}
        </div>

        <div className="grid sm:grid-cols-3 gap-3">
          {data.plans.map((p) => {
            const base = cycle === "year" ? p.yearly : p.monthly;
            const eff = cycle === "year" ? p.yearly_effective : p.monthly_effective;
            const isCur = curTier === p.tier;
            return (
              <div key={p.tier} data-testid={`plan-${p.tier}`} className={`border p-5 flex flex-col ${isCur ? "border-[#14A89A]" : "border-white/12"}`}>
                <div className="font-body text-white text-lg font-medium">{p.label}</div>
                <div className="font-body text-xs text-zinc-500 mb-3">{p.storage_gb} GB storage</div>
                <div className="mb-1">
                  {founding && <span className="font-body text-sm text-zinc-500 line-through mr-1.5">${base}</span>}
                  <span className="font-display text-2xl text-white">${eff}</span>
                  <span className="font-body text-xs text-zinc-500">/{cycle === "year" ? "yr" : "mo"}</span>
                </div>
                <button disabled={isCur || busy === p.tier} onClick={() => onPick(p.tier)} data-testid={`pick-${p.tier}`}
                  className={`mt-4 w-full py-2.5 font-body text-sm font-semibold transition-colors ${isCur ? "bg-white/5 text-zinc-500 cursor-default" : "bg-[#14A89A] text-[#0A0A0A] hover:brightness-110"}`}>
                  {busy === p.tier ? <Loader2 size={15} className="animate-spin mx-auto" /> : isCur ? "Current plan" : curTier ? "Switch here" : "Subscribe"}
                </button>
              </div>
            );
          })}
        </div>

        <div className="border border-white/10 bg-white/[0.02] p-4 mt-3 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="font-body text-white text-sm">{data.studio.label}</div>
            <div className="font-body text-xs text-zinc-500">Need more than 1 TB? We'll set you up personally.</div>
          </div>
          <a href="mailto:Sovereign.Signal.Council@proton.me?subject=Beacon%20Studio%20tier" data-testid="studio-contact" className="border border-white/15 text-zinc-200 px-4 py-2 font-body text-sm hover:bg-white/5">Contact us</a>
        </div>

        <p className="font-body text-[11px] text-zinc-600 mt-4 flex items-center gap-1.5"><Sparkles size={12} className="text-[#14A89A]" /> Every tier includes your private community (Beacon Hearth) at no extra charge.</p>
      </div>
    </div>
  );
};

export default BeaconHosting;
